import { Injectable, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {SubmissionService, IntegrationMappingService, FormViewService, IDataPullType } from '@xilo-mono/form-contracts';
import { removeMoneyFormatting } from '@xilo-mono/form-helper';
import { Subject, debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs';

interface FieldRegistration {
  destroy$: Subject<void>;
  metadataKey: string;
}

// Define allowed types explicitly
type AllowedMetadataKey = 'firstName' | 'lastName' | 'email' | 'phone';

@Injectable({
  providedIn: 'root',
})
// eslint-disable-next-line rxjs-angular/prefer-takeuntil
export class SubmissionSessionService implements OnDestroy {
  // Map to hold destroy subjects for each registered field subscription
  private fieldRegistrations = new Map<string, FieldRegistration>();
  // Define allowed keys for registration
  private readonly allowedKeys: Set<AllowedMetadataKey> = new Set([
    'firstName', 'lastName', 'email', 'phone'
  ]);

  constructor(
    private submissionService: SubmissionService,
    private integrationMappingService: IntegrationMappingService,
    private formViewService: FormViewService
  ) {}

  // eslint-disable-next-line rxjs-angular/prefer-takeuntil
  ngOnDestroy(): void {
    // Clean up all subscriptions when the service itself is destroyed (unlikely for root service, but good practice)
    this.fieldRegistrations.forEach((registration) => {
      registration.destroy$.next();
      registration.destroy$.complete();
    });
    this.fieldRegistrations.clear();
  }

  /**
   * Generates a unique key for sessionStorage based on field ID and the explicit metadata key.
   */
  private getStorageKey(field: FormlyFieldConfig, metadataKey: string): string | null {
     // Use a combination of a unique field identifier (like id) and the metadata key
     if (!field?.key) {
       const fallbackKey = field.key || metadataKey;
       return `formViewerSession_${fallbackKey}_${metadataKey}_lastUpdated`;
     }
     return `formViewerSession_${field.key}_${metadataKey}_lastUpdated`;
  }

  /**
   * Reads the last processed value from sessionStorage.
   */
  private getValueFromCache(storageKey: string | null): string | null {
    if (!storageKey) return null;
    try {
      return sessionStorage.getItem(storageKey);
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Writes the processed value to sessionStorage.
   */
  private setValueInCache(storageKey: string | null, value: string): void {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, value);
    } catch (error) {
      console.error('Error writing to sessionStorage:', error);
    }
  }

  /**
   * Performs type-specific sanitization based on dataTypeHint.
   */
  public sanitizeValue(value: any, metadataKey?: AllowedMetadataKey): string {
    let stringValue = String(value ?? '').trim();

    if (metadataKey === 'phone') {
      stringValue = stringValue.replace(/\D/g, '');
      if (stringValue.length === 11 && stringValue.startsWith('1')) {
        stringValue = stringValue.substring(1);
      }
    } else if (metadataKey === 'email') {
      stringValue = stringValue.toLowerCase();
    } else if (metadataKey === 'firstName' || metadataKey === 'lastName') {
      stringValue = stringValue.replace(/[^a-zA-Z0-9\s]/g, '');
    } else if (metadataKey === 'number') {
      stringValue = removeMoneyFormatting(String(value ?? ''));
      if (isNaN(parseFloat(stringValue))) {
        stringValue = '';
      }
    }

    return stringValue;
  }

  /**
   * Registers a specific field type (firstName, lastName, email, phone) to be handled by the service.
   */
  registerField(formControl: FormControl, field: FormlyFieldConfig, metadataKey: AllowedMetadataKey): void {

    // --- Validate Allowed Key ---
    if (!this.allowedKeys.has(metadataKey) || !field?.key || !formControl) {
      return;
    }
    // --- End Validation ---

    const storageKey = this.getStorageKey(field, metadataKey);

    if (!storageKey) {
      return;
    }

    if (this.fieldRegistrations.has(storageKey)) {
      return;
    }

    const destroy$ = new Subject<void>();
    // Store key and hint with registration
    this.fieldRegistrations.set(storageKey, { destroy$, metadataKey });

    // --- Initial Value Check ---
    const initialValue = formControl.value;
    const initialSanitized = this.sanitizeValue(initialValue);
    const cachedValue = this.getValueFromCache(storageKey);

    if (initialSanitized && initialSanitized !== (cachedValue ?? '')) {
      this.processUpdate(initialSanitized, formControl, field, metadataKey, false, storageKey);
    }
    // --- End Initial Value Check ---


    // --- valueChanges Subscription ---
    formControl.valueChanges.pipe(
      debounceTime(5000),
      distinctUntilChanged((prev, curr) => {
        const sanitizedPrev = this.sanitizeValue(prev, metadataKey);
        const sanitizedCurr = this.sanitizeValue(curr, metadataKey);
        return sanitizedPrev === sanitizedCurr;
      }),
      filter(value => value !== null && value !== undefined),
      takeUntil(destroy$)
    ).subscribe(value => {
      const sanitizedValue = this.sanitizeValue(value, metadataKey);
      this.processUpdate(sanitizedValue, formControl, field, metadataKey, false, storageKey);
    });
  }

  /**
   * Handles the blur event for a registered field.
   */
  handleBlur(formControl: FormControl, field: FormlyFieldConfig, metadataKey: AllowedMetadataKey): void {
     // --- Validate Allowed Key ---
     if (!this.allowedKeys.has(metadataKey) || !field?.key || !formControl) {
      return;
     }
     // --- End Validation ---

     const storageKey = this.getStorageKey(field, metadataKey);
     if (!storageKey) return;

     const sanitizedValue = this.sanitizeValue(formControl.value, metadataKey);
     this.processUpdate(sanitizedValue, formControl, field, metadataKey, true, storageKey);
  }


  /**
   * Unregisters a field.
   */
  unregisterField(field: FormlyFieldConfig, metadataKey: AllowedMetadataKey): void {
     // --- Validate Allowed Key ---
     if (!this.allowedKeys.has(metadataKey) || !field?.key) {
        return;
     }
     // --- End Validation ---

    const storageKey = this.getStorageKey(field, metadataKey);
    if (!storageKey) return;

    const registration = this.fieldRegistrations.get(storageKey);
    if (registration) {
      registration.destroy$.next();
      registration.destroy$.complete();
      this.fieldRegistrations.delete(storageKey);
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Error removing item from sessionStorage:', error);
      }
    }
  }


  /**
   * Core logic to process updates, check cache, and call services.
   */
  private processUpdate(sanitizedValue: string, formControl: FormControl, field: FormlyFieldConfig, metadataKey: AllowedMetadataKey, isBlurEvent: boolean, storageKey: string | null): void {
    if (!storageKey) return;

    try {
      const cachedValue = this.getValueFromCache(storageKey);

      // --- Check against Cache ---
      if (sanitizedValue === (cachedValue ?? '')) {
        if (formControl.value !== sanitizedValue) {
          formControl.patchValue(sanitizedValue, { emitEvent: false });
        }
        if (!formControl.dirty && (isBlurEvent || (cachedValue === null && sanitizedValue !== ''))) {
          formControl.markAsDirty();
        }
        return;
      }
      // --- End Check ---


      // Update form control if needed
      if (formControl.value !== sanitizedValue) {
        formControl.patchValue(sanitizedValue, { emitEvent: false });
      }
      if (!formControl.dirty) {
        formControl.markAsDirty();
      }


      // --- Call Submission Service ---
      // Use the explicitly passed metadataKey
      this.submissionService.onUpdateMetadata(
        { key: metadataKey, value: sanitizedValue },
        field
      );

      // --- Handle Email Specific Logic using dataTypeHint ---
      if (metadataKey === 'email' && field.templateOptions?.className !== 'intake-input') {
         this.formViewService.eventFired.next('New Lead');
         this.formViewService.assignAgent.next(true);
      }
      // --- Handle generic updateForm trigger ---
      if (field.templateOptions?.className !== 'xilo-input') {
        this.formViewService.updateForm.next(true);
      }
      // --- End Specific Logic ---


      // --- Conditionally Call Integration Service ---
      if (isBlurEvent && field.templateOptions?.pullDataTrigger) {
        // ... (integration pull logic remains the same) ...
        const isAEPull = field.templateOptions?.isAEPull || false;
        const pullOptions: IDataPullType = isAEPull ? { type:'DATA', loadSeconds:60 } : undefined;

        this.integrationMappingService.initDataPull(
          field.templateOptions.pullDataTrigger,
          field.templateOptions.dataPullMappingId,
          field.parent?.parent?.templateOptions?.subIndex,
          isAEPull,
          pullOptions
        );
      }

      // --- Update Cache ---
      this.setValueInCache(storageKey, sanitizedValue);

    } catch (error) {
      console.error(`Error during processUpdate for ${storageKey}:`, error);
    }
  }
}
